import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  companyDxDiagnosisAnswerApiClient,
  CompanyDxDiagnosisAnswerIndexResponse,
} from '@src/api/company/dxDiagnosis/dxDiagnosisAnswerApiClient';
import { dxDiagnosisResultApiClient } from '@src/api/company/dxDiagnosis/dxDiagnosisResultApiClient';
import { companyDxDiagnosisStepApiClient } from '@src/api/company/dxDiagnosis/dxDiagnosisStepApiClient';
import { DxDiagnosisStep } from '@src/api/types/dxDiagnosisStep';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import Button from '@src/ui/elements/button/Button';
import logger from '@src/utils/logger';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { StatusItem, StatusParent } from './components/StatusItem';
import StatusLabels from './components/StatusLabels';

function DxDiagnosisAnswers() {
  const [steps, setSteps] = useState<DxDiagnosisStep[]>([]);
  const [allUserAnswers, setAllUserAnswers] = useState<CompanyDxDiagnosisAnswerIndexResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStepbarData = async () => {
      setLoading(true);
      const response = await companyDxDiagnosisStepApiClient.index();
      handleApiResponse(
        response,
        (data: DxDiagnosisStep[]) => {
          setSteps(data);
        },
        (error) => {
          logger.error(error);
          console.error('ステップバーデータの取得に失敗しました:', error);
        },
      );
      setLoading(false);
    };
    fetchStepbarData();
  }, []);

  useEffect(() => {
    const fetchDxDiagnosisData = async () => {
      setLoading(true);
      const response = await companyDxDiagnosisAnswerApiClient.index({
        dx_diagnosis_result_id: Number(id),
      });
      handleApiResponse(
        response,
        (data) => {
          const sortedData = data.sort((a, b) => a.step.id - b.step.id);
          setAllUserAnswers(sortedData);
        },
        (error) => {
          logger.error(error);
          console.error('DX診断データを取得できませんでした', error);
        },
      );
      setLoading(false);
    };

    fetchDxDiagnosisData();
  }, [id]);

  const handleViewResult = async () => {
    const response = await dxDiagnosisResultApiClient.update(Number(id), { status: 2 });
    handleApiResponse(
      response,
      () => {
        navigate(`/dx-diagnosis/${id}/results`);
      },
      (error) => {
        logger.error('DX診断結果の更新に失敗しました:', error);
        console.error('DX診断結果の更新に失敗しました:', error);
      },
    );
  };

  if (loading) {
    return <SpinnerOverlay loading={loading} />;
  }

  return (
    <main className='bg-[#f9f9f9] pb-20'>

      <StatusParent>
        {steps.map((step, index) => (
          <StatusItem
            key={step.id}
            label={StatusLabels[index]}
            lineDirection={index === 0 ? 'right' : 'both'}
            status='completed'
            onClick={() => navigate(`/dx-diagnosis/${id}/questions/${step.id}`)}
          />
        ))}
        <StatusItem
          label='回答確認'
          lineDirection='left'
          status='in-progress-current'
          onClick={() => {}}
        />
      </StatusParent>
      <h1
        className='pt-[50px] text-center text-xl font-semibold leading-none lg:pt-[111px]'
        data-testid='title'
      >
        回答確認
      </h1>
      {allUserAnswers.map((answer, index) => (
        <div
          className='lg:px-8'
          key={index}
        >
          <div className='mx-auto max-w-[900px] py-[30px] text-xl font-semibold leading-none'>
            {index + 1}. {answer.step.name}
          </div>
          <div className='mx-auto max-w-[900px] bg-white'>
            <div className='overflow-x-auto'>
              <table className='w-full table-fixed text-sm'>
                <colgroup>
                  <col className='w-[80px]' />
                  <col className='w-2/5' />
                  <col className='w-2/5' />
                  <col className='w-1/5' />
                </colgroup>
                <thead className='border border-gray-200 bg-[#F7F7F7]'>
                  <tr>
                    <th className='h-[42px] px-4 text-xs font-semibold'>設問No.</th>
                    <th className='h-[42px] px-4 text-left text-xs font-semibold'>設問</th>
                    <th className='h-[42px] px-4 text-left text-xs font-semibold'>回答</th>
                    <th className=''></th>
                  </tr>
                </thead>
                <tbody>
                  {answer.questions
                    .sort((a, b) => a.question_id - b.question_id)
                    .map((question) => (
                      <tr
                        className='border-b border-gray-200'
                        key={question.question_id}
                      >
                        <th className='p-4 text-left align-top font-normal'>
                          {question.question_id}
                        </th>
                        <td className='break-words p-4 align-top'>{question.question_text}</td>
                        <td className='break-words p-4 align-top'>{question.answer.chosen_text}</td>
                        <td className='p-[14px] align-top'>
                          <Button
                            size='s'
                            variant='primary'
                            outline
                            fullRounded
                            onClick={() => {
                              navigate(`/dx-diagnosis/${id}/questions/${answer.step.id}`, {
                                state: { questionId: question.question_id },
                              });
                            }}
                            data-testid={`edit-answer-button-${question.question_id}`}
                          >
                            回答を修正
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {steps.length > 0 && allUserAnswers.length > 0 && (
        <div className='mt-8 flex justify-center gap-[30px]'>
          <div className='w-[172px]'>
            <Button
              size='m'
              variant='primary'
              outline
              fullRounded
              fullWidth
              onClick={() => {
                navigate(`/dx-diagnosis/${id}/questions/6`);
              }}
            >
              設問に戻る
            </Button>
          </div>
          <div className='w-[172px]'>
            <Button
              size='m'
              variant='primary'
              fullRounded
              fullWidth
              onClick={handleViewResult}
            >
              結果を見る
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}

export default DxDiagnosisAnswers;
